import React, { useState, useEffect } from "react";
import ChatWindow from "./components/ChatWindow";
import InputBar from "./components/InputBar";
import AiConfigPage from "./components/AiConfigPage";
import SiraguLogo from "./components/SiraguLogo";
import Sidebar from "./components/Sidebar";
import HomeBriefing from "./components/HomeBriefing";
import AppHeader from "./components/AppHeader";
import VoiceMode from "./components/VoiceMode";
import { authHeaders } from "./auth";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_BASE;

const SAMPLE_PROMPTS = [
  "Admission snapshot",
  "Lead conversion rate",
  "Revenue this month",
  "Applications by course",
  "Top 10 leads by score",
  "Seat fill rate by course",
];

/* ── Natural language content generator ──────────────────────────────────
   Replaces the robotic "Found X results." with contextual narrative text.
   ─────────────────────────────────────────────────────────────────────── */
function generateNarrativeContent(question, data) {
  if (data.error) return `Error: ${data.error}`;
  if (data.render_as === "text") return null;

  // Prefer the backend's real 1-2 line summary (it knows the actual data) over any
  // client-side keyword guess — this is the humanoid line shown above the chart/table.
  if (data.message) return data.message;

  const count = data.row_count ?? 0;
  const q = (question || "").toLowerCase();
  const t = data.response_time_ms
    ? ` · ${(data.response_time_ms / 1000).toFixed(1)}s`
    : "";

  if (count === 0) {
    return "No matching records found. Try broadening your search or rephrasing the query.";
  }

  // Top N pattern
  const topMatch = q.match(/top\s+(\d+)/);
  if (topMatch) {
    const n = topMatch[1];
    if (q.includes("student") || q.includes("enroll"))
      return `Here are the top ${n} students ranked by performance${t}.`;
    if (q.includes("applicant") || q.includes("application"))
      return `Top ${n} applicants based on available records${t}.`;
    if (q.includes("lead"))
      return `Here are the top ${n} leads sorted by score${t}.`;
    return `Top ${n} results from available data${t}.`;
  }

  // Snapshot / overview
  if (q.includes("snapshot") || q.includes("overview") || q.includes("summary"))
    return `Here's your current admission snapshot${t}.`;

  // Revenue / payments
  if (q.includes("revenue") || q.includes("payment") || q.includes("collection"))
    return `Here's the fee collection summary${t}.`;

  // Attendance
  if (q.includes("attendance"))
    return `Found ${count} attendance record${count !== 1 ? "s" : ""} matching your query${t}.`;

  // Applications / applicants
  if (q.includes("applicant") || q.includes("application"))
    return `Showing ${count} application${count !== 1 ? "s" : ""} matching your criteria${t}.`;

  // Leads
  if (q.includes("lead") || q.includes("conversion"))
    return `Here are the lead metrics for your query${t}.`;

  // Seat / course / fill
  if (q.includes("seat") || q.includes("fill") || q.includes("course"))
    return `Showing seat utilisation data for ${count} course${count !== 1 ? "s" : ""}${t}.`;

  // Enrollment
  if (q.includes("enroll"))
    return `Found ${count} enrolled student${count !== 1 ? "s" : ""} matching your query${t}.`;

  // Comparison / trend
  if (q.includes("compare") || q.includes("vs") || q.includes("trend"))
    return `Here's the comparative data based on your query${t}.`;

  return `Found ${count} result${count !== 1 ? "s" : ""}${t}.`;
}

let __msgSeq = 0;
const nextLocalId = () => `local-${Date.now()}-${__msgSeq++}`;

function App() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "config"
  const [sessionId, setSessionId] = useState(null);
  const [celebrated, setCelebrated] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("siragu_sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  const [historyKey, setHistoryKey] = useState(0);
  const [loadingSession, setLoadingSession] = useState(false);
  // Which ERP module the admin is asking about — the standalone chat page has no
  // embedded-page context to infer this from (unlike being opened inside a specific
  // ERP screen), so without an explicit selection every question is resolved by guessing
  // from keyword overlap alone, which reliably picks the wrong app for common phrasing
  // (e.g. "student queries" resolving to the unrelated "Student" app instead of the
  // correct "Agaram" one). Persisted so it survives reloads; null = no module selected,
  // falls back to the previous (keyword-guessing) behavior exactly as before.
  const [selectedApp, setSelectedApp] = useState(() => {
    try {
      const raw = localStorage.getItem("siragu_selected_app");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const handleSelectApp = (app) => {
    setSelectedApp(app);
    try {
      if (app) localStorage.setItem("siragu_selected_app", JSON.stringify(app));
      else localStorage.removeItem("siragu_selected_app");
    } catch {
      /* ignore storage errors */
    }
  };
  // Role/context-aware starter prompts from the backend (visitors get public/admissions
  // ones; signed-in users get role-scoped suggestions). Falls back to the static list.
  const [homeSuggestions, setHomeSuggestions] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/home/briefing`, { headers: { ...authHeaders() } });
        const data = await res.json();
        if (alive && Array.isArray(data.suggestions) && data.suggestions.length) {
          setHomeSuggestions(data.suggestions);
        }
      } catch { /* keep fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("siragu_sidebar_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const hasMessages = messages.length > 0;

  const fireConfetti = () => {
    setConfetti(true);
    setTimeout(() => setConfetti(false), 1600);
  };

  /* ── Feedback: POST thumbs up/down (+ optional dislike reason) ────────── */
  const handleFeedback = async (localId, rating, comment) => {
    // Optimistically reflect the choice in the UI regardless of backend state.
    setMessages((prev) =>
      prev.map((m) =>
        m.localId === localId ? { ...m, feedback: rating } : m
      )
    );
    const msg = messages.find((m) => m.localId === localId);
    try {
      await fetch(`${API_BASE}/chat/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          message_id: msg?.messageId ?? null,
          session_id: msg?.sessionId ?? sessionId ?? null,
          rating,
          ...(comment ? { comment } : {}),
        }),
      });
    } catch {
      /* Graceful no-op — feedback logging is best-effort until auth is live. */
    }
  };

  const handleSend = async (question) => {
    if (!question.trim() || loading) return;

    const userMsg = { role: "user", content: question, localId: nextLocalId() };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    // Returned to callers (e.g. voice mode) so they can speak the reply aloud.
    let spokenReply = "";

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          question,
          session_id: sessionId,
          ...(selectedApp ? { context: { app_id: selectedApp.id } } : {}),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error: ${res.status}`);
      }

      const data = await res.json();

      // Thread the session id back so the conversation stays continuous.
      if (data.session_id) setSessionId(data.session_id);

      const assistantMsg = {
        role: "assistant",
        localId: nextLocalId(),
        messageId: data.message_id ?? data.id ?? null,
        sessionId: data.session_id ?? sessionId ?? null,
        intent: data.intent || "DATA_QUERY",
        sql: data.sql || null,
        columns: data.columns || [],
        displayColumns: data.display_columns || data.columns || [],
        rows: data.rows || [],
        rowCount: data.row_count || 0,
        renderAs: data.render_as || "table",
        chart: data.chart || null,
        message: data.message || null,
        suggestions: data.suggestions || [],
        actions: data.actions || [],
        export: data.export || false,
        error: data.error || null,
        responseTimeMs: data.response_time_ms || 0,
        content: generateNarrativeContent(question, data),
      };

      setMessages((prev) => [...prev, assistantMsg]);

      // Build a natural spoken summary for hands-free voice mode.
      spokenReply =
        assistantMsg.message ||
        assistantMsg.content ||
        (assistantMsg.rowCount
          ? `I found ${assistantMsg.rowCount} result${
              assistantMsg.rowCount !== 1 ? "s" : ""
            }. The details are shown below.`
          : "");

      if (!celebrated && !assistantMsg.error) {
        setCelebrated(true);
        fireConfetti();
      }
    } catch (err) {
      spokenReply = "Sorry, something went wrong. Let me try that differently.";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          localId: nextLocalId(),
          content: `Hmm, let me try that differently… (${err.message})`,
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }

    return spokenReply;
  };

  /* ── New chat: clear the conversation and reset the session ───────────── */
  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setCelebrated(false);
    setLoading(false);
    setSidebarOpen(false);
    setHistoryKey((k) => k + 1); // refresh history list
  };

  /* ── Load a past conversation from history ────────────────────────────── */
  const handleOpenSession = async (id) => {
    if (!id || loadingSession) return;
    setLoadingSession(true);
    setSidebarOpen(false);
    try {
      const res = await fetch(`${API_BASE}/chat/sessions/${id}/messages`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const mapped = (data.messages || []).map((m) => {
        if (m.role === "user") {
          return { role: "user", content: m.content, localId: nextLocalId() };
        }
        return {
          role: "assistant",
          localId: nextLocalId(),
          sessionId: id,
          intent: m.intent || "DATA_QUERY",
          columns: m.columns || [],
          displayColumns: m.display_columns || m.columns || [],
          rows: m.rows || [],
          rowCount: m.row_count || (m.rows ? m.rows.length : 0),
          renderAs: m.render_as || "table",
          chart: m.chart || null,
          message: m.message || null,
          suggestions: m.suggestions || [],
          actions: m.actions || [],
          error: m.is_error || null,
          content: m.content || m.message || null,
        };
      });
      setMessages(mapped);
      setSessionId(id);
    } catch {
      // Loading failed (likely no auth yet) — keep the current view untouched.
    } finally {
      setLoadingSession(false);
    }
  };

  if (view === "config") {
    return <AiConfigPage onBack={() => setView("chat")} />;
  }

  const menuButton = (
    <button
      className="menu-btn"
      onClick={() => setSidebarOpen(true)}
      aria-label="Open chats sidebar"
      title="Chats & history"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </button>
  );

  return (
    <div className={`app${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      {menuButton}
      <AppHeader
        onOpenSettings={() => setView("config")}
        selectedApp={selectedApp}
        onSelectApp={handleSelectApp}
      />
      {confetti && <Confetti />}

      <Sidebar
        apiBase={API_BASE}
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onNewChat={handleNewChat}
        onOpenSession={handleOpenSession}
        activeSessionId={sessionId}
        refreshKey={historyKey}
      />

      {!hasMessages ? (
        <div className="welcome-view">
          <div className="welcome-content">
            {/* Brand */}
            <div className="welcome-logo">
              <SiraguLogo size={64} active={recording} />
              <span className="welcome-logo-label">Siragu</span>
            </div>

            {/* Personalized greeting (signed-in users) — above the prompt. */}
            <HomeBriefing part="greeting" apiBase={API_BASE} />

            <h1 className="welcome-title">How can I help you today?</h1>
            <p className="welcome-subtitle">
              Ask anything about admissions, leads, revenue, or student analytics.
            </p>

            <InputBar
              onSend={handleSend}
              disabled={loading}
              showQuickActions={false}
              onRecordingChange={setRecording}
              onOpenSettings={() => setView("config")}
              onOpenVoice={() => setVoiceMode(true)}
            />

            <div className="sample-prompts">
              {(homeSuggestions || SAMPLE_PROMPTS).map((prompt, i) => (
                <button
                  key={i}
                  className="sample-chip"
                  onClick={() => handleSend(prompt)}
                  disabled={loading}
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {prompt}
                </button>
              ))}
            </div>

            {/* Personalized role-scoped attention cards (signed-in users only). */}
            <HomeBriefing part="body" apiBase={API_BASE} onPick={handleSend} />
          </div>
        </div>
      ) : (
        <div className="chat-view">
          <header className="chat-header">
            <SiraguLogo size={30} active={recording} />
            <span className="chat-header-name">Siragu</span>
            {selectedApp && (
              <span className="chat-header-module" title="Questions are scoped to this module">
                {selectedApp.name}
              </span>
            )}
          </header>
          <ChatWindow
            messages={messages}
            loading={loading}
            onSend={handleSend}
            onFeedback={handleFeedback}
          />
          <div className="chat-input-area">
            <InputBar
              onSend={handleSend}
              disabled={loading}
              onRecordingChange={setRecording}
              onOpenSettings={() => setView("config")}
              onOpenVoice={() => setVoiceMode(true)}
            />
          </div>
        </div>
      )}

      {voiceMode && (
        <VoiceMode
          apiBase={API_BASE}
          onSend={handleSend}
          onClose={() => setVoiceMode(false)}
        />
      )}
    </div>
  );
}

/* ── Confetti burst on first successful answer ───────────────────────────── */
function Confetti() {
  const colors = ["#22D3EE", "#6C63FF", "#EC4899", "#F59E0B", "#10B981"];
  const pieces = Array.from({ length: 26 });
  return (
    <div className="confetti-layer" aria-hidden="true">
      {pieces.map((_, i) => (
        <span
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            background: colors[i % colors.length],
            animationDelay: `${Math.random() * 0.4}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
}

export default App;
